import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Calculator, ShoppingBasket } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_FORMATS = [
  { code: 'rect',    name: 'Rectangle',     dim: '300x250',  cpm: 22.80 },
  { code: 'halfpage',name: 'Half page',     dim: '300x600',  cpm: 34.44 },
  { code: 'billboard',name: 'Billboard',    dim: '970x250',  cpm: 45.84 },
  { code: 'inarticle',name: 'In article',   dim: '580x300',  cpm: 57.36 },
  { code: 'sponsored',name: 'Sponz. članak', dim: '48 sati', cpm: 1550, flat: true },
];

/**
 * Quick-purchase modal triggered by clicking the "Ovdje može biti vaš oglas"
 * placeholder on the public site. Lets the visitor pick a format, days and
 * estimated impressions, then bounces to /oglasavanje with prefill.
 */
export default function AdQuickPurchaseModal({ slot, size, onClose }) {
  const navigate = useNavigate();
  const [format, setFormat] = useState(QUICK_FORMATS[2].code);
  const [days, setDays] = useState(14);
  const [impressions, setImpressions] = useState(20000);
  const f = QUICK_FORMATS.find(x => x.code === format) || QUICK_FORMATS[0];
  const total = f.flat ? f.cpm : (impressions / 1000) * f.cpm * (days / 14);

  function buy() {
    const params = new URLSearchParams({
      add: format, days: String(days), impressions: String(impressions), slot: slot || '', size: size || '',
    });
    navigate(`/oglasavanje?${params.toString()}`);
    onClose?.();
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="ad-quick-modal">
      <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Vaš oglas — brza ponuda</h3>
            <p className="text-xs text-gray-500">Pozicija: <strong>{slot || size || '—'}</strong></p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-white" data-testid="ad-quick-close"><X size={20} /></button>
        </div>

        <label className="block text-xs text-gray-500 mb-1">Format</label>
        <select value={format} onChange={e => setFormat(e.target.value)} data-testid="ad-quick-format"
                className="w-full mb-3 px-3 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded text-sm">
          {QUICK_FORMATS.map(x => <option key={x.code} value={x.code}>{x.name} ({x.dim}) · €{x.cpm.toFixed(2)} {x.flat ? '/kom' : 'CPM'}</option>)}
        </select>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Trajanje (dana)</label>
            <div className="flex gap-1">
              {[7, 14, 30, 60].map(d => (
                <button key={d} onClick={() => setDays(d)} data-testid={`ad-quick-days-${d}`}
                        className={`flex-1 py-1.5 text-xs rounded ${days === d ? 'bg-red-600 text-white' : 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}>{d}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Impresije</label>
            <select value={impressions} onChange={e => setImpressions(parseInt(e.target.value))} data-testid="ad-quick-impressions"
                    className="w-full px-2 py-1.5 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded text-sm">
              {[10000, 20000, 50000, 100000, 250000, 500000].map(p => <option key={p} value={p}>{p.toLocaleString()}</option>)}
            </select>
          </div>
        </div>

        <div className="bg-gray-100 dark:bg-gray-800 rounded p-3 flex items-center justify-between mb-4">
          <span className="text-xs flex items-center gap-1.5 text-gray-500"><Calculator size={12} /> Procjena</span>
          <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400" data-testid="ad-quick-total">€ {total.toFixed(2)}</span>
        </div>

        <button onClick={buy} className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded inline-flex items-center justify-center gap-2"
                data-testid="ad-quick-buy">
          <ShoppingBasket size={16} /> Nastavi na košaricu
        </button>
        <p className="text-[10px] text-center text-gray-500 mt-2">Pojednostavljen tijek — košarica + plaćanje na sljedećoj stranici. Promo kod možete unijeti u koraku plaćanja.</p>
      </div>
    </div>
  );
}
